// ─── Which reports are about the same event ──────────────────────────────────
//
// The article window summarises an EVENT, not one article, so it first has to
// find the other reports of it. No new source: the pool is what the app
// already fetched · the feed on screen and the company feeds of the companies
// the article is about. Three conditions, all required (DECISIONS.md,
// 2026-09-25):
//
//   1 · time     · published within WINDOW_H hours of the opened article
//   2 · company  · both are about the same company · a shared Finnhub symbol,
//                  or, when either carries none, the same company named in
//                  both headlines
//   3 · headline · the two headlines share enough of their words: at least
//                  MIN_SHARED meaningful words, and they cover at least
//                  MIN_OVERLAP of the shorter headline
//
// Press releases never join a cluster (utils/newsMatch.js). A source appears
// once · its newest report · so one outlet's three updates do not read as
// three voices. At most MAX_ITEMS, newest first, the opened article always in.

import { isPressRelease } from "./newsMatch";

export const WINDOW_H = 36;
export const MIN_SHARED = 3;
export const MIN_OVERLAP = 0.5;
export const MAX_ITEMS = 6;

const STOP = new Set(("the a an and or of to in on for with at by from as is are was were be been it its this that these those " +
  "after before over under into about amid says said say new more most less than up down out off its their his her " +
  "stock stocks shares share market markets report reports reported company companies inc corp ltd plc co " +
  "why how what who when will would could should may might can here there just now today week year " +
  // Index names and market vocabulary say which market, not which event: two
  // unrelated round-ups both say "Dow Jones index" (the ChartMill/Yahoo pair,
  // 2026-09-26). They do not count as shared words.
  "dow jones s&p 500 nasdaq index indices indexes russell 2000 wall street composite nyse " +
  "session trading traders investors equities rally rallies slip slips falls rise rises gains losses").split(" "));

// Lists and round-ups are not one event: "stocks moving", "movers", "stocks
// to watch", "top 5 stocks", "best stocks". They never join a cluster and
// never get a brief (David, 2026-09-26).
const ROUNDUP_RE = /\b(stocks?\s+(moving|to\s+watch|to\s+buy|making|on\s+the\s+move|in\s+motion)|movers|top\s+\d+\s+\w*\s*stocks?|best\s+\w*\s*stocks?|\d+\s+\w*\s*stocks?\s+(to|that|for|you)|what'?s\s+moving|trending\s+stocks?|stock\s+market\s+today|market\s+wrap|weekly\s+wrap|morning\s+brief|daily\s+brief|top\s+movers)\b/i;
export function isRoundup(article) {
  return ROUNDUP_RE.test(`${article?.title || ""}`);
}

export function headlineTokens(title) {
  return new Set(String(title || "").toLowerCase()
    .replace(/['’]s\b/g, "")
    .split(/[^a-z0-9$%.]+/)
    .map(w => w.replace(/^[.]+|[.]+$/g, ""))
    .filter(w => w && (w.length >= 3 || /\d/.test(w)) && !STOP.has(w)));
}

export function headlineSimilar(a, b) {
  const A = headlineTokens(a), B = headlineTokens(b);
  if (!A.size || !B.size) return false;
  let shared = 0;
  for (const w of A) if (B.has(w)) shared++;
  return shared >= MIN_SHARED && shared / Math.min(A.size, B.size) >= MIN_OVERLAP;
}

function sameCompany(a, b, companyWords) {
  const sa = new Set(a.symbols || []), sb = b.symbols || [];
  if (sa.size && sb.length) return sb.some(s => sa.has(s));
  // One side carries no symbols: both headlines must name the same company.
  const ta = headlineTokens(a.title), tb = headlineTokens(b.title);
  return companyWords.some(w => ta.has(w) && tb.has(w));
}

// `companyWords`: lowercase company names/tickers the opened article is about.
export function clusterFor(article, pool = [], { companyWords = [] } = {}) {
  if (!article) return [];
  if (isRoundup(article)) return [article];      // a list is never an event
  const t0 = article.datetime || 0;
  const key = a => a.id || a.url;
  const seen = new Set([key(article)]);
  const out = [article];
  const bySource = new Map([[article.source, article]]);
  const cands = pool
    .filter(a => a && !seen.has(key(a)) && a.title && !isPressRelease(a) && !isRoundup(a))
    .filter(a => Math.abs((a.datetime || 0) - t0) <= WINDOW_H * 3600)
    .filter(a => sameCompany(article, a, companyWords) && headlineSimilar(article.title, a.title))
    .sort((a, b) => (b.datetime || 0) - (a.datetime || 0));
  for (const a of cands) {
    if (seen.has(key(a))) continue;
    seen.add(key(a));
    if (bySource.has(a.source)) continue;       // one voice per outlet
    bySource.set(a.source, a);
    out.push(a);
    if (out.length >= MAX_ITEMS) break;
  }
  return out.sort((a, b) => (b.datetime || 0) - (a.datetime || 0));
}

// A stable id for the cluster, for the summary cache. Its members decide it:
// a new report joining the event is a different set, and a new summary.
export function clusterId(items) {
  const ids = items.map(a => String(a.id || a.url)).sort().join("|");
  let h = 5381;
  for (let i = 0; i < ids.length; i++) h = ((h * 33) ^ ids.charCodeAt(i)) >>> 0;
  return `cluster:${h.toString(36)}:${items.length}`;
}
